/**
 * History Rail — the review bar (HR-2).
 *
 * A row inside `.pb-panel-center`, above `.pb-iframe-wrap`. Not an island over
 * the canvas: it takes vertical space and pushes the canvas down, the same way
 * the rail takes horizontal space. Word's version-history banner, and for the
 * same reason — it has to be unmissable while being read-only chrome.
 *
 * It carries exactly ONE commit control (contract §3). Per-row Restore buttons
 * are what made the modal need a delegated single-flight guard against two
 * whole-project restores racing; there is nothing to race here because there is
 * one button and it acts on whatever the canvas is showing.
 *
 * HR-2 ships the bar and its states. The button is wired in HR-3, so until then
 * it is present but disabled with its reason on it — never a live control that
 * silently does nothing.
 */

import { createButton } from '../components.js';

const HOST_SELECTOR = '.pb-panel-center';
const CANVAS_SELECTOR = '.pb-iframe-wrap';

export function mountReviewBar({ onExit, onRestore, canRestore, onMode, onVisualDiff } = {}) {
  const host = document.querySelector(HOST_SELECTOR);
  const canvas = host?.querySelector(CANVAS_SELECTOR);
  if (!host || !canvas) return null;

  const bar = document.createElement('div');
  bar.className = 'pb-rail-review';
  bar.setAttribute('data-role', 'review-bar');
  // The bar announces what the canvas is showing, and that is a live region:
  // the canvas changing under someone who cannot see it is exactly the case
  // where the announcement is the only signal.
  bar.setAttribute('aria-live', 'polite');
  bar.hidden = true;

  const inner = document.createElement('div');
  inner.className = 'pb-rail-review-inner';

  const label = document.createElement('span');
  label.className = 'pb-rail-review-label';

  const detail = document.createElement('span');
  detail.className = 'pb-rail-review-detail';

  /** Which version's fallback the note describes; see set(). */
  let noteVersion = null;

  const note = document.createElement('span');
  note.className = 'pb-rail-review-note';
  note.hidden = true;

  /**
   * ONE segmented control where the modal had three differently-named buttons:
   * "See what changed" in a banner, "Compare to now", and "Visual diff vs vN" —
   * three names for two ideas, with nothing anywhere saying that the first two
   * are STRUCTURAL and the third is PIXELS.
   */
  let mode = 'this';
  const seg = document.createElement('span');
  seg.className = 'pb-rail-review-seg';
  seg.setAttribute('role', 'group');
  seg.setAttribute('aria-label', 'Compare mode');
  for (const [key, text, tip] of [
    ['this', 'This version', 'Show this version as it was saved'],
    ['changes', 'Changes', 'Outline the sections this version changed']
  ]) {
    const b = createButton({
      variant: 'bare',
      label: text,
      tooltip: tip,
      dataset: { mode: key },
      onClick: () => {
        if (mode === key) return;
        mode = key;
        for (const other of seg.querySelectorAll('button')) {
          other.setAttribute('aria-pressed', String(other === b));
        }
        onMode?.(mode);
      }
    });
    b.setAttribute('aria-pressed', String(key === 'this'));
    seg.appendChild(b);
  }

  // Pixels stay one click away rather than being a separate mystery button in a
  // row of version actions. The tooltip carries the distinction the modal never
  // made: visual comparison needs TWO SAVED versions, which is why it cannot
  // compare against unsaved work.
  const visual = createButton({
    variant: 'bare',
    className: 'pb-rail-review-visual',
    label: 'Visual diff',
    tooltip: 'Compare pixels against the latest save (saved versions only)',
    onClick: () => onVisualDiff?.()
  });

  const changes = document.createElement('span');
  changes.className = 'pb-rail-review-changes';
  changes.hidden = true;

  const spacer = document.createElement('span');
  spacer.className = 'pb-rail-review-spacer';

  const restore = createButton({
    variant: 'primary',
    size: 'sm',
    label: 'Restore this version',
    className: 'pb-rail-review-restore',
    dataset: { action: 'restore' },
    onClick: () => onRestore?.()
  });
  restore.disabled = true;

  const exit = createButton({
    variant: 'bare',
    className: 'pb-rail-review-exit',
    label: 'Exit',
    ariaLabel: 'Stop viewing this version and return to your work',
    onClick: () => onExit?.()
  });

  inner.append(label, detail, note, changes, spacer, seg, visual, restore, exit);
  bar.appendChild(inner);
  canvas.before(bar);

  return {
    el: bar,

    /**
     * @param {object} state
     * @param {'live'|'loading'|'previewing'|'error'} state.status
     */
    set(state) {
      if (!state || state.status === 'live') {
        bar.hidden = true;
        return;
      }
      bar.hidden = false;
      bar.classList.toggle('is-error', state.status === 'error');

      if (state.status === 'error') {
        label.textContent = state.error || 'Could not show that version';
        detail.textContent = '';
        restore.disabled = true;
        return;
      }
      if (state.status === 'loading') {
        label.textContent = `Loading v${state.version}…`;
        detail.textContent = '';
        restore.disabled = true;
        return;
      }

      label.textContent = `Viewing v${state.version}`;
      const parts = ['read-only'];
      if (state.label) parts.unshift(state.label);
      if (state.when) parts.unshift(state.when);
      detail.textContent = parts.join(' · ');

      // A control that looks live and does nothing is worse than one that says
      // why it cannot act, so the reason rides the button itself. The modal's
      // read-only guard fired only AFTER the click — a trap with a receipt.
      const blocked = onRestore ? canRestore?.() || null : 'Restoring is not available here';
      restore.disabled = Boolean(blocked);
      if (blocked) {
        restore.title = blocked;
        restore.setAttribute('aria-disabled', 'true');
      } else {
        restore.removeAttribute('title');
        restore.removeAttribute('aria-disabled');
      }
      // Clear the note only when the VERSION changed. The fallback arrives from
      // the shell on a different, asynchronous path than these status updates,
      // so the two interleave unpredictably — clearing unconditionally means
      // whichever lands second wins and the notice is usually the loser. Tying
      // it to a version makes the order irrelevant. (HR-2 browser pass: the
      // notice was produced correctly and never once appeared.)
      if (noteVersion !== state.version) {
        noteVersion = null;
        note.textContent = '';
        note.hidden = true;
        changes.textContent = '';
        changes.hidden = true;
      }
    },

    /**
     * The page that did not exist yet. Arrives separately because only the shell
     * can see which pages the target version has, and it must be SAID: falling
     * back silently means the user asked for About, got Home, and nothing
     * explained the difference.
     */
    note(pageFallback, version = null) {
      noteVersion = version;
      if (!pageFallback?.used) {
        note.textContent = '';
        note.hidden = true;
        return;
      }
      const asked = pageFallback.requestedName || pageFallback.requested || null;
      const shown = pageFallback.usedName || null;

      // Say nothing rather than something absurd. Page IDS differ while page
      // NAMES routinely match — a page recreated with the same title, or a live
      // store sitting on a differently-keyed page — and the notice then reads
      // "Home didn't exist yet — showing Home", which is worse than silence
      // because it makes the user doubt a canvas that is perfectly correct.
      // (HR-4's browser pass produced exactly that sentence.)
      if (!asked || !shown || asked === shown) {
        note.textContent = '';
        note.hidden = true;
        return;
      }
      note.textContent = `${asked} didn't exist yet — showing ${shown}`;
      note.hidden = false;
    },

    get mode() {
      return mode;
    },

    /**
     * The "Since vN: 3 sections changed · 1 removed (not shown)" line.
     *
     * Its OWN element, not the fallback notice's. They carry different facts,
     * both can be true at once, and HR-2 already paid for sharing one slot
     * between two writers on different async paths.
     */
    changes(text) {
      changes.textContent = text || '';
      changes.hidden = !text;
    },

    destroy() {
      bar.remove();
    }
  };
}
